"use client";

import * as React from "react";
import Image from "next/image";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@workspace/ui/components/dialog";
import { cn } from "@workspace/ui/lib/utils";
import { homeAssets } from "../../_utils/home-assets";
import {
  EventNames,
  SectionTypes,
  sendEvent,
  withSectionType,
} from "@/lib/utils/analytics";

export type ExpertCompactCard = {
  image: string;
  name: string;
  role: string;
  quote: string;
  channel?: string;
  hasVideo?: boolean;
  imageUnoptimized?: boolean;
  /** Background position for desktop CSS bg card. Defaults to "center center". */
  desktopImagePosition?: string;
  /** Full custom background value for the desktop card image area. Overrides desktopImagePosition. */
  desktopBackground?: string;
};

export type ExpertRecommendedSectionProps = {
  title: string;
  videoSources?: {
    webm: string;
    mp4: string;
  };
  mobileCards: ExpertCompactCard[];
  mobileVideoPoster?: string;
};

function PlayBadge({
  className,
  variant = "desktop",
}: {
  className?: string;
  variant?: "mobile" | "desktop";
}) {
  return (
    <div
      className={cn(
        "inline-flex items-center text-white",
        variant === "mobile"
          ? "gap-[8.34px] rounded-[74.45px] border border-white/20 bg-black/[0.19] px-[14px] py-[10px] backdrop-blur-[24px]"
          : "h-11 gap-[7.76px] rounded-[74.45px] bg-black/[0.19] px-4 py-4",
        className,
      )}
    >
      <PlayIcon className="size-[18.51px]" />
      <span className={variant === "mobile" ? "title-16" : "title-18"}>
        Watch
      </span>
    </div>
  );
}

export function ExpertRecommendedSection({
  title,
  videoSources,
  mobileCards,
  mobileVideoPoster,
}: ExpertRecommendedSectionProps) {
  const [isMobileVideoOpen, setIsMobileVideoOpen] = React.useState(false);
  const hasTrackedVideoPlay = React.useRef(false);

  const trackVideoPlay = React.useCallback(() => {
    if (hasTrackedVideoPlay.current) return;
    hasTrackedVideoPlay.current = true;
    sendEvent(
      EventNames.VIDEO_PLAYED,
      withSectionType(SectionTypes.RECOMMENDED_BY_CLINICIANS),
    );
  }, []);

  return (
    <>
      <section
        id="recommended-by-clinicians"
        data-section-type="recommended_by_clinicians"
        className="bg-base-1 px-6 py-20 xl:px-20 xl:py-[120px]"
      >
        <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-8 xl:gap-12">
          <div className="mx-auto w-full max-w-[760px] xl:mx-0 xl:max-w-none">
            <h2
              id="expert-recommended-title"
              data-testid="expert-recommended-title"
              className="title-32 text-left text-primary xl:max-w-[716px] xl:title-40"
            >
              {title === "Recommended by Clinicians" ? (
                <>
                  Recommended by
                  <br className="hidden xl:block" /> Clinicians
                </>
              ) : (
                title
              )}
            </h2>
          </div>

          {/* Mobile cards */}
          <div className="mx-auto flex w-full max-w-[760px] flex-col gap-5 xl:hidden">
            {mobileCards.map((card, index) => (
              <article key={card.name} className="overflow-hidden rounded-2xl">
                {card.imageUnoptimized ? (
                  <div
                    className="relative flex h-[320px] flex-col items-start justify-end gap-[10px] self-stretch overflow-hidden px-6 pb-5 pt-6"
                    style={{
                      borderRadius: "8px 8px 0 0",
                    }}
                  >
                    <Image
                      src={card.image}
                      alt={card.name}
                      fill
                      unoptimized
                      className="object-cover"
                      style={{ objectPosition: "center 58%" }}
                      sizes="(min-width: 1280px) 0px, min(100vw - 48px, 760px)"
                    />
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 69.69%, rgba(0, 0, 0, 0.80) 100%)",
                      }}
                    />
                    <p
                      id={`expert-recommended-card-mobile-${index + 1}-name`}
                      data-testid={`expert-recommended-card-mobile-${index + 1}-name`}
                      className="relative title-18 text-base-1"
                    >
                      {card.name}
                    </p>
                    <p
                      id={`expert-recommended-card-mobile-${index + 1}-role`}
                      data-testid={`expert-recommended-card-mobile-${index + 1}-role`}
                      className="relative captitle-10 text-base-2"
                    >
                      {card.role}
                    </p>
                  </div>
                ) : (
                  <div className="relative h-80 w-full">
                    {card.hasVideo ? (
                      <button
                        type="button"
                        onClick={() => setIsMobileVideoOpen(true)}
                        id={`expert-recommended-card-mobile-${index + 1}-video-button`}
                        data-testid={`expert-recommended-card-mobile-${index + 1}-video-button`}
                        className="absolute inset-0 block w-full cursor-pointer text-left"
                        aria-label={`Watch ${card.name} video`}
                      >
                        <video
                          poster={card.image}
                          muted
                          playsInline
                          preload="none"
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      </button>
                    ) : (
                      <Image
                        src={card.image}
                        alt={card.name}
                        fill
                        className="object-cover"
                        sizes="(min-width: 1280px) 0px, min(100vw - 48px, 760px)"
                      />
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent p-6 pb-5">
                      <p
                        id={`expert-recommended-card-mobile-${index + 1}-name`}
                        data-testid={`expert-recommended-card-mobile-${index + 1}-name`}
                        className="title-18 text-base-1"
                      >
                        {card.name}
                      </p>
                      <div className="mt-2 flex items-center gap-1.5">
                        <p
                          id={`expert-recommended-card-mobile-${index + 1}-role`}
                          data-testid={`expert-recommended-card-mobile-${index + 1}-role`}
                          className="captitle-10 text-base-2"
                        >
                          {card.role}
                        </p>
                        {card.channel ? (
                          <>
                            <span
                              id={`expert-recommended-card-mobile-${index + 1}-separator`}
                              data-testid={`expert-recommended-card-mobile-${index + 1}-separator`}
                              className="captitle-10 text-base-2"
                            >
                              •
                            </span>
                            <Image
                              src={homeAssets.expertRecommended.youtubeIcon}
                              alt=""
                              aria-hidden
                              width={18}
                              height={18}
                              className="size-[18px]"
                            />
                            <p
                              id={`expert-recommended-card-mobile-${index + 1}-channel`}
                              data-testid={`expert-recommended-card-mobile-${index + 1}-channel`}
                              className="captitle-10 text-base-2"
                            >
                              {card.channel}
                            </p>
                          </>
                        ) : null}
                      </div>
                    </div>
                    {card.channel ? (
                      <button
                        type="button"
                        onClick={() => setIsMobileVideoOpen(true)}
                        id={`expert-recommended-card-mobile-${index + 1}-watch-button`}
                        data-testid={`expert-recommended-card-mobile-${index + 1}-watch-button`}
                        className="absolute right-4 top-4 cursor-pointer"
                        aria-label={`Watch ${card.name} video`}
                      >
                        <PlayBadge variant="mobile" />
                      </button>
                    ) : null}
                  </div>
                )}

                <div className="bg-light-olive p-6">
                  <p
                    id={`expert-recommended-card-mobile-${index + 1}-quote`}
                    data-testid={`expert-recommended-card-mobile-${index + 1}-quote`}
                    className="font-highlight text-[18px] font-normal leading-[24px] text-primary"
                  >
                    {card.quote}
                  </p>
                </div>
              </article>
            ))}
          </div>

          {/* Desktop cards — 3 equal columns */}
          <div className="hidden gap-5 xl:grid xl:grid-cols-3">
            {mobileCards.map((card, index) => (
              <article
                key={card.name}
                className="flex flex-col overflow-hidden rounded-2xl bg-[#F3F4ED]"
              >
                <div
                  className="relative flex h-[360px] flex-col justify-end gap-[10px] px-6 pb-5 pt-6"
                  style={
                    card.desktopBackground
                      ? {
                          background: card.desktopBackground.replace(
                            /<path-to-image>/g,
                            card.image,
                          ),
                        }
                      : undefined
                  }
                >
                  {!card.desktopBackground ? (
                    <>
                      <Image
                        src={card.image}
                        alt={card.name}
                        fill
                        className="object-cover"
                        style={{
                          objectPosition: card.desktopImagePosition ?? "center center",
                        }}
                        sizes="(min-width: 1280px) calc((100vw - 200px) / 3), 0px"
                      />
                      <div
                        className="absolute inset-0"
                        style={{
                          background:
                            "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 65%, rgba(0, 0, 0, 1.00) 100%)",
                        }}
                      />
                    </>
                  ) : null}
                  <p
                    id={`expert-recommended-card-desktop-${index + 1}-name`}
                    data-testid={`expert-recommended-card-desktop-${index + 1}-name`}
                    className="relative title-20 text-white"
                  >
                    {card.name}
                  </p>
                  <p
                    id={`expert-recommended-card-desktop-${index + 1}-role`}
                    data-testid={`expert-recommended-card-desktop-${index + 1}-role`}
                    className="relative captitle-10 text-white/80"
                  >
                    {card.role}
                  </p>
                </div>

                <div className="flex flex-1 flex-col justify-start p-6">
                  <p
                    id={`expert-recommended-card-desktop-${index + 1}-quote`}
                    data-testid={`expert-recommended-card-desktop-${index + 1}-quote`}
                    className="font-highlight text-[18px] leading-[24px] text-primary"
                  >
                    {card.quote}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {videoSources ? (
        <Dialog open={isMobileVideoOpen} onOpenChange={setIsMobileVideoOpen}>
          <DialogContent
            className="max-w-[90vw] overflow-hidden border-0 bg-black p-0 xl:hidden"
            showCloseButton
            closeButtonClass="right-3 top-3 z-10 text-white"
          >
            <DialogTitle
              id="expert-recommended-video-dialog-title"
              data-testid="expert-recommended-video-dialog-title"
              className="sr-only"
            >
              Expert recommended video
            </DialogTitle>
            <video
              id="expert-recommended-video-dialog-player"
              data-testid="expert-recommended-video-dialog-player"
              poster={mobileVideoPoster}
              autoPlay
              controls
              playsInline
              className="aspect-[9/16] w-full"
              onPlay={trackVideoPlay}
            >
              <source src={videoSources.webm} type="video/webm" />
              <source src={videoSources.mp4} type="video/mp4" />
            </video>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="19"
      height="19"
      viewBox="0 0 19 19"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 9.25419C0 4.14588 4.14588 0 9.25419 0C14.3625 0 18.5084 4.14588 18.5084 9.25419C18.5084 14.3625 14.3625 18.5084 9.25419 18.5084C4.14588 18.5084 0 14.3625 0 9.25419ZM12.9551 9.2543L7.40263 5.08992V13.4187L12.9551 9.2543Z"
        fill="white"
      />
    </svg>
  );
}

